import React, { useContext, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { addDays, format } from "date-fns";
import MessageContainer from "./MessageContainer";

import useQuotation from "../../../hooks/useQuotation";
import useCommission from "../../../hooks/useCommissions";
import useInvoice from "../../../hooks/useInvoice";

import useChat from "../../../hooks/useChat";
import ChatSessionContext from "../../../contexts/ChatSessionContext";
import AppContext from "../../../AppContext";
import { PAGE_STATE, langComponent } from "../../../constants";
import CommissionContext from "../../../contexts/CommissionContext";
import _ from "lodash";

const DraftSubmitted = ({ commissionId, submissionId }) => {
  const { currentSession: session } = useContext(ChatSessionContext);
  const { user, getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.chat);
  const TranslationCommon = getLang(langComponent.common);
  const { getQuotation, approveQuotation, rejectQuotation } = useQuotation();
  const {
    getCommission,
    querySubmissions,
    getSubmissionFiles,
    downloadSubmissionFiles,
    patchSubmission,
    patchCommission,
  } = useCommission();
  const { createInvoice } = useInvoice();
  const { createChat, CHAT_TYPE } = useChat();
  const [detail, setDetail] = useState(undefined);
  const detailRef = useRef(undefined);
  const [isOwner, setIsOwner] = useState(undefined);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const { commissions: commission, setCommissions: setCommission } =
    useContext(CommissionContext);

  useEffect(() => {
    if (!commissionId || !submissionId) return;
    fetchData();
    socket.on("commissions:submission-changed", handleSubmissionChanged);
    return () => {
      socket.on("commissions:submission-changed", handleSubmissionChanged);
    };
  }, []);

  const fetchData = async () => {
    const [commissionResult, submissionResult, submissionFilesResult] =
      await Promise.allSettled([
        getCommission(commissionId),
        querySubmissions(commissionId, { _id: submissionId }),
        getSubmissionFiles(commissionId, submissionId),
      ]);

    const { draft, link, createdOn, acceptedOn } =
      submissionResult.value.data[0];

    _setDetail({
      commissionNo: commissionResult.value.no,
      drafts: commissionResult.value.drafts,
      draftNo: draft,
      link,
      files: submissionFilesResult.value,
      createdOn,
      acceptedOn,
      isOwner: commissionResult.value.createdBy === user._id,
    });
    setPageState(PAGE_STATE.IDLE);
  };

  const handleSubmissionChanged = async (body) => {
    const { _id, acceptedOn } = JSON.parse(body);
    if (submissionId !== _id) return;

    _setDetail({
      ...detailRef.current,
      acceptedOn,
    });
  };

  const _setDetail = (detail) => {
    console.log("detail", detail);
    setDetail(detail);
    detailRef.current = detail;
  };

  const handleConfirmClick = async () => {
    const result = await patchSubmission(commissionId, submissionId, {
      acceptedOn: new Date(),
    });

    _setDetail({
      ...detail,
      acceptedOn: result.acceptedOn,
    });

    let _commission = _.cloneDeep(commission);
    _commission.submissions = _commission.submissions.map((draft) => {
      if (draft._id === result._id) {
        return result;
      }

      return draft;
    });

    // * Check if final draft
    if (detail.draftNo >= detail.drafts) {
      _commission.status = 4;

      patchCommission(commissionId, {
        status: 4,
      });
    }

    if (detail.draftNo < detail.drafts) {
      _commission.status = 2;

      patchCommission(commissionId, {
        status: 2,
      });
    }

    setCommission(_commission);
  };

  const getStatus = () => {
    if (!detail.acceptedOn && !detail.isOwner) {
      return (
        <span
          className="chat-quotation-content-status"
          onClick={handleConfirmClick}
        >
          {Translation("Confirm")}
        </span>
      );
    }

    return null;
  };

  const handleConfirm = async () => {
    // * Approve quotation
    await approveQuotation(id);

    // * Create Invoice
    const invoice = await createInvoice({
      commission: detail.commission._id,
      quotation: id,
    });

    // * Assign invoice to commission
    patchCommission(detail.commission._id, {
      invoice: invoice._id,
      status: 1,
    });

    // * Create Chat
    createChat(session._id, CHAT_TYPE.INVOICE, { invoiceId: invoice._id });
  };

  const handleReject = () => rejectQuotation(id);

  const handleDownloadfile = async (file) => {
    const [link] = await downloadSubmissionFiles(commissionId, submissionId, [
      file._id,
    ]);

    const res = await fetch(link);
    const blob = await res.blob();
    const href = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.download = file.name;
    a.href = href;
    a.click();
    a.href = "";
  };

  if (pageState === PAGE_STATE.LOADING) {
    return (
      <MessageContainer alignment={"center"}>
        <div className="chat-quotation">Loading</div>
      </MessageContainer>
    );
  }

  if (pageState === PAGE_STATE.ERROR) {
    return null;
  }

  const { commissionNo, draftNo, link, files, createdOn, acceptedOn } =
    detail || {};

  return (
    <MessageContainer alignment={"center"}>
      <div className="chat-quotation">
        <span className="chat-subtitle">
          - {Translation("Draft submitted")} -
        </span>
        <div className="chat-quotation-content">
          <h3 className="chat-quotation-content__header">
            {Translation("Draft no")} {draftNo}
          </h3>
          <div className="chat-quotation-content-info">
            <span>{Translation("Order Id")}</span>
            <span className="chat-quotation-content-info__value">
              {commissionNo || "-"}
            </span>
            <span>{Translation("Submitted on")}</span>
            <span className="chat-quotation-content-info__value">
              {format(new Date(createdOn), "dd MMM yyyy")}
            </span>
            {acceptedOn && (
              <>
                <span>{Translation("Accepted on")}</span>
                <span className="chat-quotation-content-info__value">
                  {format(new Date(acceptedOn), "dd MMM yyyy")}
                </span>
              </>
            )}
            {link && (
              <>
                <span>{Translation("Link")}</span>
                <a className="chat-quotation-content-info__value" href={link}>
                  {link}
                </a>
              </>
            )}
            {files && (
              <>
                <span style={{ gridColumn: "1 / 3" }}>
                  {Translation("Files")}
                </span>
                <div style={{ gridColumn: "1 / 3" }}>
                  {files.map((file) => {
                    return (
                      <div
                        key={file._id}
                        onClick={() => handleDownloadfile(file)}
                      >
                        {file.name}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            <div className="chat-quotation-content-status">{getStatus()}</div>
          </div>
        </div>
      </div>
    </MessageContainer>
  );
};

DraftSubmitted.propTypes = {};

export default DraftSubmitted;
